/* KD9 — /app/acompanamiento (User) — Solicitud de acompañamiento funcional.
   La sesión se realiza fuera de la plataforma; KD9 administra el proceso. */
import React, { useEffect, useRef, useState } from "react";
import { PageShell } from "@/pages/dashboard/RolePages";
import { Heart, Phone, Video, Shield, Clock, Send, CheckCircle2, FileText, AlertCircle } from "lucide-react";
import api from "@/lib/api";

const REQUEST_KEY_STORAGE = "kd9-accompaniment-request-key";

const persistentRequestKey = () => {
  const current = window.sessionStorage.getItem(REQUEST_KEY_STORAGE);
  if (current) return current;
  const next = crypto.randomUUID();
  window.sessionStorage.setItem(REQUEST_KEY_STORAGE, next);
  return next;
};

const rotateRequestKey = () => {
  const next = crypto.randomUUID();
  window.sessionStorage.setItem(REQUEST_KEY_STORAGE, next);
  return next;
};

const AcompanamientoDashboard = ({ mode = "accompaniment" }) => {
  const isVideo = mode === "video";
  const [sessionType, setSessionType] = useState(isVideo ? "video_external" : "phone");
  const [duration, setDuration] = useState(30);
  const [offerings, setOfferings] = useState([]);
  const [offeringId, setOfferingId] = useState("");
  const [formData, setFormData] = useState({});
  const [reason, setReason] = useState("");
  const [notes, setNotes] = useState("");
  const [attachment, setAttachment] = useState("");
  const [error, setError] = useState("");
  const [rechargeUrl, setRechargeUrl] = useState("");
  const [created, setCreated] = useState(null);
  const [busy, setBusy] = useState(false);
  const busyRef = useRef(false);
  const [mine, setMine] = useState([]);
  const [version, setVersion] = useState(0);
  const [idempotencyKey, setIdempotencyKey] = useState(persistentRequestKey);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [mineRes, catalogRes] = await Promise.all([
          api.get("/api/acompanamiento/mine"),
          api.get("/api/acompanamiento/catalog"),
        ]);
        if (!cancelled) {
          const matchesMode = (row) => isVideo ? row.session_type === "video_external" : row.session_type !== "video_external";
          const ownFlow = mineRes.data.filter(matchesMode);
          const ownCatalog = catalogRes.data.filter(matchesMode);
          setMine(ownFlow);
          setOfferings(ownCatalog);
          if (!offeringId && ownCatalog.length) {
            const first = ownCatalog[0];
            setOfferingId(first.id); setSessionType(first.session_type); setDuration(first.duration_min);
          }
        }
      } catch (_) { /* silent */ }
    })();
    return () => { cancelled = true; };
  }, [version, offeringId, isVideo]);

  const selectedOffering = offerings.find((item) => item.id === offeringId);

  const submit = (e) => {
    e.preventDefault();
    setError(""); setRechargeUrl(""); setCreated(null);
    if (!selectedOffering) { setError("Selecciona un acompañamiento disponible."); return; }
    if (reason.trim().length < 3) { setError("Cuéntanos el motivo de la consulta (mínimo 3 caracteres)."); return; }
    if (attachment && !/^https?:\/\/.+\.(pdf|png|jpg|jpeg|webp)(\?.*)?$/i.test(attachment)) {
      setError("El archivo debe ser un enlace público a PDF o imagen (png/jpg/webp).");
      return;
    }
    if (busyRef.current) return;
    busyRef.current = true;
    setBusy(true);
    api.post("/api/acompanamiento/request", {
      offering_id: offeringId,
      session_type: sessionType,
      duration_min: Number(duration),
      reason: reason.trim(),
      notes: notes.trim() || undefined,
      attachment_url: attachment.trim() || undefined,
      form_data: { ...formData, reason: reason.trim() },
      idempotency_key: idempotencyKey,
    })
      .then(({ data }) => {
        setCreated(data);
        setIdempotencyKey(rotateRequestKey());
        setReason(""); setNotes(""); setAttachment(""); setFormData({});
        setVersion((v) => v + 1);
      })
.catch((e) => {
        const detail = e?.response?.data?.detail;
        if (detail?.code === "INSUFFICIENT_ENERGY") {
          setError("Energía KD9 insuficiente para este acompañamiento");
          setRechargeUrl(detail.redirect || "/app/energia-kd");
          return;
        }
        if (detail?.code === "WALLET_UNAVAILABLE") {
          setError("Tu billetera de Energía KD9 no está disponible en este momento.");
          return;
        }
        setError("No pudimos completar la solicitud. Inténtalo de nuevo; no se realizó ningún descuento.");
      })
      .finally(() => { busyRef.current = false; setBusy(false); });
  };



  const statusLabel = (status) => ({
    requested: "Solicitado", pending_information: "Pendiente de información",
    pending_assignment: "Disponible en Bolsa de Acompañamientos",
    assigned: "Asignado a Consultor", accepted: "Aceptado",
    rejected: "Rechazado", scheduled: "Agendado", in_progress: "En proceso",
    completed: "Completado", cancelled: "Cancelado", returned: "Devuelto con observación",
  }[status] || String(status || "").replace(/_/g, " "));

  return (
    <PageShell kicker={isVideo ? "Usuario · Videollamadas KD9" : "Usuario · Acompañamiento"} title={isVideo ? "AGENDAR VIDEOLLAMADA KD9" : "SOLICITAR ACOMPAÑAMIENTO"}
      desc={isVideo ? "Videollamada KD9 gestionada exclusivamente con Energía KD9 o como servicio gratuito." : "Acompañamiento personal gestionado exclusivamente con Energía KD9 o como servicio gratuito."}
      testId={isVideo ? "videollamada-page" : "acompanamiento-page"}>
      <div className="grid lg:grid-cols-3 gap-6">
        {/* LEFT — info */}
        <div className="lg:col-span-1 space-y-4">
          <div className="kd9-card p-5">
            <div className="flex items-center gap-2 mb-2"><Heart size={16} style={{ color: "var(--kd9-gold)" }} />
              <div className="font-cinzel kd9-gold-text text-base tracking-wider">¿QUÉ ES?</div>
            </div>
            <p className="font-cormorant text-base" style={{ color: "var(--kd9-text-soft)" }}>
              El acompañamiento KD9 es una sesión guiada, personal y confidencial con un consultor entrenado
              para apoyarte en tu proceso interno.
            </p>
          </div>
          <div className="kd9-card p-5">
            <div className="flex items-center gap-2 mb-2"><Clock size={16} style={{ color: "var(--kd9-gold)" }} />
              <div className="font-cinzel kd9-gold-text text-base tracking-wider">¿CÓMO FUNCIONA?</div>
            </div>
            <ol className="font-cormorant text-base space-y-1 list-decimal pl-5" style={{ color: "var(--kd9-text-soft)" }}>
              <li>Envías tu solicitud.</li>
              <li>Un Administrador asigna un consultor.</li>
              <li>Se coordina la sesión por un medio externo (Zoom / WhatsApp / llamada).</li>
              <li>Realizas la sesión fuera de KD9.</li>
              <li>Registras seguimiento aquí.</li>
            </ol>
          </div>
          <div className="kd9-card p-5">
            <div className="flex items-center gap-2 mb-2"><Shield size={16} style={{ color: "var(--kd9-gold)" }} />
              <div className="font-cinzel kd9-gold-text text-base tracking-wider">CONFIDENCIALIDAD</div>
            </div>
            <p className="font-cormorant text-base" style={{ color: "var(--kd9-text-soft)" }}>
              Todo lo compartido en la sesión es estrictamente confidencial. Tiempo de respuesta habitual: 24–48h.
            </p>
          </div>
          <div className="kd9-card p-5" style={{ background: "linear-gradient(135deg, rgba(122,14,23,0.10), transparent)" }}>
            <div className="flex items-start gap-2">
              <AlertCircle size={14} style={{ color: "#FCA5A5" }} className="mt-1 shrink-0" />
              <p className="font-cormorant text-sm" style={{ color: "var(--kd9-text)" }}>
                <strong>Importante:</strong> la sesión <u>no se realiza dentro</u> de KD9. Se ejecuta por el medio externo
                que el consultor asignado te indique.
              </p>
            </div>
          </div>
        </div>

        {/* CENTER — form */}
        <div className="lg:col-span-2">
          {created ? (
            <div className="kd9-card p-8" data-testid="acomp-success">
              <div className="flex items-center gap-3 mb-4">
                <CheckCircle2 size={22} style={{ color: "#22C55E" }} />
                <div className="font-cinzel kd9-gold-text text-2xl tracking-wider">SOLICITUD ENVIADA</div>
              </div>
              <div className="grid sm:grid-cols-2 gap-3 mb-5">
                <div><div className="text-[10px] font-mont tracking-widest uppercase" style={{ color: "var(--kd9-text-soft)" }}>Número</div><div className="font-mont font-semibold" data-testid="acomp-request-id">#{created.id.slice(-8)}</div></div>
                <div><div className="text-[10px] font-mont tracking-widest uppercase" style={{ color: "var(--kd9-text-soft)" }}>Fecha</div><div className="font-mont">{new Date(created.created_at).toLocaleString()}</div></div>
                <div><div className="text-[10px] font-mont tracking-widest uppercase" style={{ color: "var(--kd9-text-soft)" }}>Tipo</div><div className="font-mont">{created.session_type === "phone" ? "Llamada telefónica" : "Videollamada externa"}</div></div>
                <div><div className="text-[10px] font-mont tracking-widest uppercase" style={{ color: "var(--kd9-text-soft)" }}>Duración</div><div className="font-mont">{created.duration_min} min</div></div>
                <div className="sm:col-span-2"><div className="text-[10px] font-mont tracking-widest uppercase" style={{ color: "var(--kd9-text-soft)" }}>Estado</div>
                  <span className="px-2.5 py-1 rounded-full text-[10px] font-mont tracking-widest uppercase font-semibold" style={{ background: "rgba(212,175,55,0.18)", color: "var(--kd9-gold)", border: "1px solid rgba(212,175,55,0.4)" }} data-testid="acomp-status">
                    {statusLabel(created.status)}
                  </span>
                </div>
              </div>
              <p className="font-cormorant text-base mb-5" style={{ color: "var(--kd9-text)" }}>
{created.energy_cost > 0
                  ? `Se descontaron ${new Intl.NumberFormat("es-CO").format(created.energy_cost)} KD de tu wallet y la solicitud quedó disponible para Consultores habilitados.`
                  : "Tu solicitud gratuita quedó disponible para Consultores habilitados."}
              </p>
              <button onClick={() => setCreated(null)} className="kd9-btn-outline px-5 py-2.5 rounded-lg text-[10px]" data-testid="acomp-new-btn">
                Enviar otra solicitud
              </button>
            </div>
          ) : (
            <form onSubmit={submit} className="kd9-card p-6" data-testid="acomp-form">
              {error && (
                <div className="mb-4 px-4 py-3 rounded-lg border text-sm font-mont"
                  style={{ color: "#FCA5A5", borderColor: "rgba(122,14,23,0.4)", background: "rgba(122,14,23,0.10)" }} data-testid="acomp-error">
                  <span>{error}</span>
                  {rechargeUrl && (
                    <button type="button" className="kd9-btn-outline ml-3 px-3 py-1.5 rounded-lg" onClick={() => window.location.assign(rechargeUrl)}>
                      Recargar energía
                    </button>
                  )}
                </div>
              )}

              {/* Catálogo universal publicado por Dirección */}
              <div className="mb-5">
                <div className="text-[11px] font-mont tracking-[0.25em] uppercase mb-2" style={{ color: "var(--kd9-text-soft)" }}>Acompañamiento disponible</div>
                <div className="grid sm:grid-cols-2 gap-3">
                  {offerings.map((item) => {
                    const Icon = item.session_type === "phone" ? Phone : Video;
                    return <label key={item.id} className="flex items-start gap-3 p-3 rounded-lg border cursor-pointer"
                      style={{ borderColor: offeringId === item.id ? "var(--kd9-gold)" : "var(--kd9-border)", background: offeringId === item.id ? "rgba(212,175,55,0.08)" : "transparent" }}>
                      <input type="radio" name="offering" checked={offeringId === item.id} onChange={() => { setOfferingId(item.id); setSessionType(item.session_type); setDuration(item.duration_min); setFormData({}); }} />
                      <Icon size={16} style={{ color: "var(--kd9-gold)" }} />
                      <span className="font-mont text-sm"><strong>{item.name}</strong><br/><small>{item.duration_min} min · {item.is_free ? "Gratuito · 0 KD" : `${new Intl.NumberFormat("es-CO").format(item.energy_cost)} Energía KD9`}</small></span>
                    </label>
                  })}
                </div>
                {!offerings.length && <div className="font-mont text-sm mt-2" style={{ color: "#FCA5A5" }}>No hay acompañamientos publicados actualmente.</div>}
              </div>

              {/* Motivo */}
              <div className="mb-4">
                <label className="text-[11px] font-mont tracking-[0.25em] uppercase mb-2 block" style={{ color: "var(--kd9-text-soft)" }}>Motivo de la consulta *</label>
                <textarea rows={3} className="kd9-input pl-4 py-3" value={reason} onChange={(e) => setReason(e.target.value)} required data-testid="acomp-reason" placeholder="¿Qué te trae al acompañamiento?" />
              </div>

              {(selectedOffering?.required_fields || []).filter((field) => field.key !== "reason").map((field) => (
                <div className="mb-4" key={field.key}>
                  <label className="text-[11px] font-mont tracking-[0.2em] uppercase mb-2 block">{field.label}{field.required ? " *" : ""}</label>
                  {field.field_type === "textarea"
                    ? <textarea className="kd9-input p-3" required={field.required} value={formData[field.key] || ""} onChange={(e) => setFormData({ ...formData, [field.key]: e.target.value })}/>
                    : <input type={["date", "time", "email", "phone"].includes(field.field_type) ? (field.field_type === "phone" ? "tel" : field.field_type) : "text"} className="kd9-input pl-4" required={field.required} value={formData[field.key] || ""} onChange={(e) => setFormData({ ...formData, [field.key]: e.target.value })}/>}
                </div>
              ))}

              {/* Observaciones */}
              <div className="mb-4">
                <label className="text-[11px] font-mont tracking-[0.25em] uppercase mb-2 block" style={{ color: "var(--kd9-text-soft)" }}>Observaciones (opcional)</label>
                <textarea rows={2} className="kd9-input pl-4 py-3" value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="acomp-notes" placeholder="Preferencias de horario, contexto adicional…" />
              </div>

              {/* Attachment URL */}
              <div className="mb-6">
                <label className="text-[11px] font-mont tracking-[0.25em] uppercase mb-2 block" style={{ color: "var(--kd9-text-soft)" }}>
                  <FileText size={12} className="inline mr-1" /> Archivo (opcional · URL pública PDF / imagen)
                </label>
                <input className="kd9-input pl-4" placeholder="https://drive.google.com/…" value={attachment} onChange={(e) => setAttachment(e.target.value)} data-testid="acomp-attachment" />
              </div>

              <button type="submit" disabled={busy || !selectedOffering} className="kd9-btn-primary w-full py-4 rounded-xl text-[12px] flex items-center justify-center gap-3" style={{ opacity: (busy || !selectedOffering) ? 0.6 : 1 }} data-testid="acomp-submit">
                <Send size={14} /> {busy ? "Creando solicitud…" : `Solicitar acompañamiento${selectedOffering && !selectedOffering.is_free ? ` · ⚡ ${new Intl.NumberFormat("es-CO").format(selectedOffering.energy_cost)} KD` : " · Gratuito"}`}
              </button>
            </form>
          )}

          {/* Historial mínimo */}
          {mine.length > 0 && (
            <div className="mt-6 kd9-card overflow-hidden" data-testid="acomp-history">
              <div className="px-6 py-3 border-b font-cinzel kd9-gold-text tracking-wider text-sm" style={{ borderColor: "var(--kd9-border)" }}>MIS SOLICITUDES</div>
              {mine.map((m, i) => (
                <div key={m.id} className="px-6 py-4 border-b last:border-0 flex items-center gap-4" style={{ borderColor: "var(--kd9-border)" }} data-testid={`acomp-hist-${i}`}>
                  <div className="flex-1">
                    <div className="font-mont text-sm font-semibold">#{m.id.slice(-8)} · {m.session_type === "phone" ? "Llamada" : "Video"} · {m.duration_min} min</div>
                    <div className="text-[11px] font-mont" style={{ color: "var(--kd9-text-soft)" }}>{m.created_at ? new Date(m.created_at).toLocaleString() : ""} · &ldquo;{m.reason.slice(0, 60)}{m.reason.length > 60 ? "…" : ""}&rdquo;</div>
                  </div>
                  <span className="px-2.5 py-1 rounded-full text-[10px] font-mont tracking-widest uppercase font-semibold" style={{ background: "rgba(212,175,55,0.18)", color: "var(--kd9-gold)", border: "1px solid rgba(212,175,55,0.4)" }}>
                    {statusLabel(m.status)}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </PageShell>
  );
};

export default AcompanamientoDashboard;
